import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import * as S from './FormInventoryList.styles'
import { actions } from '../../InventoryState'
import { FormCreateInventory } from '../FormCreateInventory/FormCreateInventory'
import { ManageBadges } from '../ManageBadges/ManageBadges'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { useEffect } from 'react'
import { actions as appActions } from '../../../state'

interface Props {
  clickCreate: boolean
  setClickCreate: (value: boolean) => void
  clickBadges: boolean
  setClickBadges: (value: boolean) => void
}

export const FormInventoryList = ({
  clickCreate, setClickCreate, clickBadges, setClickBadges,
}: Props) => {
  const dispatch = useDispatch()
  const fcs = useSelector(selectFulfillmentCenters)
  const {
    register, watch, handleSubmit, getValues, formState: { errors },
  } = useForm()

  const prepareFcs = () =>
    fcs.map((fulfillmentCenter) => ({
      id: fulfillmentCenter.id,
      description: fulfillmentCenter.name,
    }))

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  const onSubmit = () => {
    const fulfillmentCenterId = getValues('fulfillmentCenterId')
    const initialDate = getValues('startDate')
    const finalDate = getValues('endDate')

    dispatch(actions.getInventory({
      fulfillmentCenterId,
      initialDate,
      finalDate,
    }))
  }

  if (clickCreate) return <FormCreateInventory setClickCreate={setClickCreate} />
  if (clickBadges) return <ManageBadges setClickBadges={setClickBadges} />

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <S.FilterContainer>
        <S.CustomSelectWrapper>
          <S.CustomTextField
            name='startDate'
            id='startDate'
            label='startDate.inventory'
            type='date'
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...register('startDate', {
                required: 'Campo obrigatório',
              }),
              'data-testid': 'startDate-test',
              max: dayjs().format('YYYY-MM-DD'),
            }}
            error={!!errors.startDate}
            helperText={errors.startDate?.message}
          />
          <S.CustomTextField
            name='endDate'
            id='endDate'
            label='endDate.inventory'
            type='date'
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...register('endDate', {
                required: 'Campo obrigatório',
                validate: (value) => {
                  const startDate = watch('startDate')
                  if (!startDate) {
                    return 'Data de início é obrigatória'
                  }
                  if (dayjs(value).isBefore(dayjs(startDate))) {
                    return 'Data de fim não pode ser menor que a de início'
                  }
                  const differenceInDays = dayjs(value).diff(dayjs(startDate), 'day')
                  return differenceInDays <= 30 || 'A diferença entre as datas deve ser de no máximo 30 dias'
                },
              }),
              'data-testid': 'endDate-test',
              max: watch('startDate') ? dayjs(watch('startDate')).add(30, 'day').format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            }}
            error={!!errors.endDate}
            helperText={errors.endDate?.message}
          />
          <S.CustomSelect
            name='fulfillmentCenterId'
            inputProps={{ ...register('fulfillmentCenterId', { required: 'Campo obrigatório' }), 'data-testid': 'fc-inventory-list-select' }}
            autoComplete='off'
            id='fulfillmentCenterId'
            label='Selecione um FC'
            variant='outlined'
            options={prepareFcs()}
            error={!!errors.fulfillmentCenterId}
            helperText={errors.fulfillmentCenterId?.message}
            optionNone={false}
            type='text'
          />
          <S.CustomButton
            title='Buscar'
            type='submit'
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            data-testid='search-inventory-button'
          />
          <S.CustomButton
            title='Criar'
            bgColor='confirm'
            hoverColor='confirmHover'
            letterColor='white'
            onClick={() => setClickCreate(true)}
            data-testid='create-inventory-button'
          />
          <S.CustomButtonBadges
            title='Configurar crachás'
            bgColor='orange'
            hoverColor='lightOrange'
            letterColor='white'
            onClick={() => setClickBadges(true)}
            data-testid='badge-inventory-button'
          />
        </S.CustomSelectWrapper>
      </S.FilterContainer>
    </form>
  )
}
